import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { getAuth } from 'firebase/auth';
import { collection, doc, getDocs, getFirestore, setDoc } from 'firebase/firestore';
import { Calendar, ExternalLink, Mail, Phone, Trash2, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import DataGridLayout from '../../components/layout/DataGridLayout';
import { Button } from '../../components/ui/button';
import { DataTable } from '../../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import VerificationToggle from '../../components/ui/VerificationToggle';
import { useToast } from '../../hooks/use-toast';
import { app } from '../../firebase';
import { record } from 'zod';

// Sample data for career gap job registrations
const MOCK_REGISTRATIONS = [
    {
        id: '0SEyBK2VTMflDhDEH8LV',
        email: 'chiranjeevisarvepalli0105@gmail.com',
        phone: '+918978570105',
        yearPassedOut: 2021,
        careerGap: 2,
        isVerified: true,
    },
    {
        id: '1',
        email: 'rocky11noone@gmail.com',
        phone: '+918217390087',
        yearPassedOut: 2020,
        careerGap: 0,
        isVerified: true,
    },
    {
        id: '2',
        email: 'salmansid397@gmail.com',
        phone: '+919380291674',
        yearPassedOut: 2019,
        careerGap: 1,
        isVerified: false,
    },
    {
        id: '3',
        email: 'nitenkumar58@gmail.com',
        phone: '+916006247815',
        yearPassedOut: 2018,
        careerGap: 3,
        isVerified: true,
    }
];

const CareerGapRegistrations = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRegistration, setSelectedRegistration] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const db = getFirestore(app);
    const auth = getAuth(app);
    const { toast } = useToast();
    const [registrations, setRegistrations] = useState([]);
    const [allrecords, setRecords] = useState([]);

    useEffect(() => {
        // Inside your component  
        const fetchEnquires = async () => {

            try {
                const snapshot = await getDocs(collection(db, 'jobGuarantee'));
                const contactUsData: any = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setRecords(contactUsData);
            } catch (error) {
                toast({
                    title: "Error Fetching ContactUs",
                    description: error.message,
                    variant: "destructive",
                });
            }
        };

        fetchEnquires();
    }, [])
    // Define table columns  
    const columns: any[] = [
        {
            accessorKey: 'email',
            header: 'Email',
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-blue-500" />
                    <span>{row.getValue('email')}</span>
                </div>
            ),
        },
        {
            accessorKey: 'phone',
            header: 'Phone',
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-green-500" />
                    <span>{row.getValue('phone')}</span>
                </div>
            ),
        },
        {
            accessorKey: 'yearPassedOut',
            header: 'Year Passed Out',
        },
        {
            accessorKey: 'careerGap',
            header: 'Career Gap (Years)',
        },
        {
            accessorKey: 'isVerified',
            header: 'Is Verified',
            cell: ({ row }) => (
                <div className="flex justify-center">
                    <VerificationToggle
                        isVerified={row.original.isVerified}
                        onToggle={() => handleToggleVerification(row.original.id)}
                    />
                </div>
            ),
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewDetails(row.original)}
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                    >
                        <ExternalLink size={18} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(row.original)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                        <Trash2 size={18} />
                    </Button>
                </div>
            ),
        }
    ];



    const fetchRegistrations = async () => {

        try {
            const snapshot = await getDocs(collection(db, 'jobGuarantee'));
            const contactUsData: any = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            setRecords(contactUsData);
        } catch (error) {
            toast({
                title: "Error Fetching ContactUs",
                description: error.message,
                variant: "destructive",
            });
        }
    };

    const handleViewDetails = (registration) => {
        setSelectedRegistration(registration);
    };

    const handleDeleteClick = (registration) => {
        setSelectedRegistration(registration);
        setShowDeleteConfirm(true);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
    };

    const handleRefresh = () => {
        setIsLoading(true);
        fetchRegistrations();
        setTimeout(() => {
            setIsLoading(false);
            toast({
                title: 'Refreshed',
                description: 'Registration data has been refreshed successfully.',
            });
        }, 800);
    };

    const handleToggleVerification = async (id) => {
        setRecords(prevRegistrations =>
            prevRegistrations.map(registration =>
                registration.id === id
                    ? { ...registration, isVerified: !registration.isVerified }
                    : registration
            )
        );
        const record = allrecords.find(x => x.id === id);

        try {
            const courseRef = doc(db, 'jobGuarantee', id);
            await setDoc(courseRef, { ...record, isVerified: !record.isVerified });
            toast({
                title: 'Verification Status Updated',
                description: 'The verification status has been updated successfully.',
            });
        } catch (error) {
            toast({
                title: 'Verification Failed',
                description: 'The verification update has failed.',
            });
        }
    };

    const handleDelete = () => {
        if (!selectedRegistration) return;

        setRecords(prevRegistrations =>
            prevRegistrations.filter(registration => registration.id !== selectedRegistration.id)
        );

        toast({
            title: 'Entry Deleted',
            description: 'The registration has been removed successfully.',
        });

        setShowDeleteConfirm(false);
        setSelectedRegistration(null);
    };

    // Filter registrations based on search query  
    const filteredRegistrations = allrecords.filter(registration =>
        registration.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        registration.phone.includes(searchQuery)
    );

    return (
        <>
            <DataGridLayout
                title="Career Gap Job Registrations"
                subtitle="Manage career gap job registrations"
                description="Track registrations and their verification status"
                searchPlaceholder="Search by email or phone..."
                onSearch={handleSearch}
                onRefresh={handleRefresh}
                onAdd={() => toast({
                    title: 'Add Functionality',
                    description: 'Add new registration feature would be implemented here.',
                })}
            >
                <DataTable
                    columns={columns}
                    data={filteredRegistrations}
                    noResultsMessage="No registrations found"
                    isLoading={isLoading}
                />
            </DataGridLayout>

            {/* Registration Details Dialog */}
            {selectedRegistration && (
                <Dialog open={!!selectedRegistration && !showDeleteConfirm} onOpenChange={() => setSelectedRegistration(null)}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Registration Details</DialogTitle>
                            <DialogDescription>
                                Complete information about the selected registration.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="email" className="text-right">
                                    Email
                                </Label>
                                <Input id="email" value={selectedRegistration.email} readOnly className="col-span-3" />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="phone" className="text-right">
                                    Phone
                                </Label>
                                <Input id="phone" value={selectedRegistration.phone} readOnly className="col-span-3" />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="yearPassedOut" className="text-right">
                                    Year Passed Out
                                </Label>
                                <Input id="yearPassedOut" value={selectedRegistration.yearPassedOut} readOnly className="col-span-3" />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="careerGap" className="text-right">
                                    Career Gap (Years)
                                </Label>
                                <Input id="careerGap" value={selectedRegistration.careerGap} readOnly className="col-span-3" />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="status" className="text-right">
                                    Status
                                </Label>
                                <div className="col-span-3">
                                    <VerificationToggle
                                        isVerified={selectedRegistration.isVerified}
                                        onToggle={() => handleToggleVerification(selectedRegistration.id)}
                                    />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setSelectedRegistration(null)}>
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this registration? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        {selectedRegistration && (
                            <div className="text-center p-4 border rounded-md bg-muted/50">
                                <p className="font-semibold">{selectedRegistration.email}</p>
                                <p className="text-sm text-muted-foreground">{selectedRegistration.phone}</p>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="sm:justify-between">
                        <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CareerGapRegistrations;